import { createFileRoute, Link } from "@tanstack/react-router";
import { z } from "zod";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { listPublishedPosts } from "@/lib/blog.functions";
import { SiteHeader, SiteFooter } from "@/components/blog/SiteHeader";
import { CursorGlow } from "@/components/CursorGlow";
import type { BlogPostWithTags, BlogTagRow } from "@/lib/blog.server";
import { markdownToPlainText, truncate } from "@/lib/blog/markdown";
import { BlogSidebar } from "@/components/blog/BlogSidebar";
import { HomeHero } from "@/components/HomeHero";
import { Testimonials } from "@/components/Testimonials";
import { ExitIntentPopup } from "@/components/ExitIntentPopup";
import { MagicMyMessaging } from "@/components/MagicMyMessaging";

const SITE_URL = "https://threedaylaunch.com";
const PAGE_TITLE = "Coaching Website Design Blog: Convert Clients and Grow";
const PAGE_DESCRIPTION =
  "Coaching website design tips to boost credibility, speed, and conversions. Learn white space, branding, CTAs, and lead-gen tactics on the Three Day Launch blog.";

// Use Supabase Storage image transformations to serve smaller, optimized
// thumbnails on the blog listing (faster LCP, lower bandwidth).
function optimizedImage(url: string, width = 800): string {
  if (!url) return url;
  const rendered = url.replace(
    "/storage/v1/object/public/",
    "/storage/v1/render/image/public/",
  );
  const sep = rendered.includes("?") ? "&" : "?";
  return `${rendered}${sep}width=${width}&quality=75&resize=cover`;
}

const searchSchema = z.object({ tag: z.string().optional() });

export const Route = createFileRoute("/blog/")({
  validateSearch: (s) => searchSchema.parse(s),
  loaderDeps: ({ search }) => ({ tag: search.tag ?? null }),
  loader: async ({ deps }) => {
    const posts = await listPublishedPosts({ data: { tag: deps.tag } });
    return { posts, tag: deps.tag };
  },
  head: ({ loaderData }) => {
    const recent = (loaderData?.posts ?? []).slice(0, 10);
    return {
      meta: [
        { title: PAGE_TITLE },
        { name: "description", content: PAGE_DESCRIPTION },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        { property: "og:title", content: PAGE_TITLE },
        { property: "og:description", content: PAGE_DESCRIPTION },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `${SITE_URL}/blog` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: PAGE_TITLE },
        { name: "twitter:description", content: PAGE_DESCRIPTION },
      ],
      links: [{ rel: "canonical", href: `${SITE_URL}/blog` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Three Day Launch Blog",
            url: `${SITE_URL}/blog`,
            description: PAGE_DESCRIPTION,
            publisher: {
              "@type": "Organization",
              name: "Three Day Launch",
              url: SITE_URL,
            },
            blogPost: recent.map((p) => ({
              "@type": "BlogPosting",
              headline: p.title,
              url: `${SITE_URL}/blog/${p.slug}`,
              datePublished:
                p.published_at && new Date(p.published_at).getTime() > Date.now()
                  ? new Date().toISOString()
                  : p.published_at,
              dateModified: p.updated_at,
              description: p.excerpt || truncate(markdownToPlainText(p.content), 160),
              image: p.cover_image_url ?? undefined,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
              { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
            ],
          }),
        },
      ],
    };
  },
  component: BlogIndex,
});


function BlogIndex() {
  const data = Route.useLoaderData();
  const posts = data.posts as BlogPostWithTags[];
  const tag = data.tag;

  // Build a unique tag list from posts for the filter chip row
  const allTags: BlogTagRow[] = Array.from(
    new Map(
      posts.flatMap((p) => p.tags.map((t) => [t.slug, t] as const))
    ).values()
  );

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border bg-gradient-to-br from-primary/10 via-background to-background">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 h-80 w-80 rounded-full bg-primary/20 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-primary/10 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.04] [background-image:linear-gradient(to_right,currentColor_1px,transparent_1px),linear-gradient(to_bottom,currentColor_1px,transparent_1px)] [background-size:48px_48px]"
        />
        <div className="relative mx-auto max-w-5xl px-4 py-20 md:py-28">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            WEBSITE DESIGN FOR COACHES
          </div>
          <h1 className="mt-5 text-4xl font-bold tracking-tight text-foreground md:text-6xl">
            The Three Day Launch website design for coaches{" "}
            <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              blog
            </span>
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground md:text-xl">
            Strategies you'll actually want to use on coaching website design and growing an online coaching business.
          </p>
        </div>
      </section>

      <main className="relative w-full overflow-hidden pl-[45px] pr-[45px] pt-[90px] pb-[90px] mb-[0px]">
        <CursorGlow opacity={0.22} />
        {tag && (
          <header className="mb-10">
            <p className="text-sm font-medium uppercase tracking-wider text-primary">Filtered</p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
              Posts tagged "{tag}"
            </h2>
          </header>
        )}


        <div className="grid gap-10 lg:grid-cols-[1fr_300px]">
          <div>
            {posts.length === 0 ? (
              <p className="text-muted-foreground">No posts yet — check back soon.</p>
            ) : (
              <section aria-label="Blog posts" className="grid gap-8 sm:grid-cols-2">
                {posts.map((p) => (
                  <article
                    key={p.id}
                    className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card transition hover:border-primary/40"
                  >
                    <Link to="/blog/$slug" params={{ slug: p.slug }} className="block">
                      {p.cover_image_url && (
                        <img
                          src={optimizedImage(p.cover_image_url, 800)}
                          srcSet={`${optimizedImage(p.cover_image_url, 400)} 400w, ${optimizedImage(p.cover_image_url, 800)} 800w, ${optimizedImage(p.cover_image_url, 1200)} 1200w`}
                          sizes="(min-width: 1024px) 360px, (min-width: 640px) 45vw, 90vw"
                          alt={p.cover_image_alt ?? `${p.title} — Three Day Launch coaching website design blog`}
                          title={p.cover_image_alt ?? p.title}
                          loading="lazy"
                          decoding="async"
                          width={800}
                          height={450}
                          className="aspect-[16/9] w-full object-cover"
                        />
                      )}
                      <div className="p-6">
                        <h2 className="text-xl font-semibold text-foreground group-hover:text-primary">
                          {p.title}
                        </h2>
                        <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">
                          {p.excerpt || truncate(markdownToPlainText(p.content), 180)}
                        </p>
                        <span className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition group-hover:opacity-90">
                          Read now
                          <span aria-hidden="true">→</span>
                        </span>
                      </div>
                    </Link>
                  </article>
                ))}
              </section>
            )}
          </div>

          <BlogSidebar />
        </div>

        {/* Crawler/no-JS fallback */}
        <noscript>
          <section>
            <h2>All posts</h2>
            <ul>
              {posts.map((p) => (
                <li key={p.id}>
                  <a href={`/blog/${p.slug}`}>
                    <strong>{p.title}</strong>
                  </a>
                  {" — "}
                  {p.excerpt || truncate(markdownToPlainText(p.content), 180)}
                </li>
              ))}
            </ul>
          </section>
        </noscript>
      </main>
      <Testimonials />
      <HomeHero />
      <MagicMyMessaging />
      <SiteFooter />
      <ExitIntentPopup />
    </div>
  );
}



// Render markdown safely (kept here because vite-bundled in SSR uses ESM)
export function BlogMarkdown({ content }: { content: string }) {
  return (
    <div className="prose prose-neutral max-w-none dark:prose-invert">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </div>
  );
}
